import { Typography } from "antd";
import {
  CoinPriceTextFormat,
  CustomerLogo,
  MonthlyFilters,
  PaymentHistoryState,
  PaymentHistoryStatus,
  ProviderLogo,
} from "@lib/components/MiniComponents/Others";
import { FormattedMessage, useIntl } from "react-intl";
import { ApiParams, TransactionProxyStatus, TransactionType } from "@lib/types";
import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { apiGetTransactions } from "@lib/api/transaction";
import { useQuery } from "@tanstack/react-query";
import MiniList, {
  MiniListProps,
} from "@lib/components/MiniComponents/Dashboard/MiniList";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { PaymentHistoryDrawer } from "@lib/components/PageComponents/PaymentHistory";
import { AppAvatar } from "@lib/components/AppComponents";

type Props = Partial<MiniListProps>;

export default function AwaitingApprovalPayments(props: Props) {
  const { messages } = useIntl();
  const [openDrawer, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState<TransactionType>();

  useEffect(() => {
    if (!openDrawer) setSelected(null);
  }, [openDrawer]);

  const toggle = (status?: boolean) => {
    if (typeof status !== "undefined") setDrawerOpen(status);
    else setDrawerOpen(!openDrawer);
  };

  const handleSelect = (item: TransactionType) => {
    setSelected(item);
    toggle(true);
  };

  const params = useMemo(() => {
    return {
      join: ["customer", "provider"],
      sort: "externalId,DESC",
      s: [
        {
          $or: [
            { status: { $eq: TransactionProxyStatus.Pending } },
            { status: { $eq: TransactionProxyStatus.Initialized } },
          ],
        },
      ],
    } as ApiParams;
  }, []);

  const paymentHistoryQuery = useQuery({
    queryKey: [
      "payment-history-transaction",
      "payment-provider-dashboard-awaiting-approval-transactions",
      JSON.stringify(params),
    ],
    queryFn: () => apiGetTransactions({ ...params }),
    enabled: !!params,
  });
  const apiData = paymentHistoryQuery?.data?.data?.data;

  const firstDay = apiData?.[0]?.createdAt;
  const lastDay = apiData?.[apiData?.length - 1]?.createdAt;

  return (
    <>
      <MiniList
        {...props}
        cardProps={{
          borderFull: true,
        }}
        title={messages["common.thoseAwaitingApproval"] as string}
        footer={
          <FormattedMessage
            id="common.pendingPaymentCount"
            values={{ count: apiData?.length || 0 }}
          />
        }
        data={(apiData || [])?.map((item) => ({
          onClick: () => {
            handleSelect(item);
          },
          title: (
            <StyledFlex style={{ gap: 4, justifyContent: "space-between" }}>
              <CoinPriceTextFormat
                amount={item.amount}
                showCurrency={item?.currency}
                align="left"
              />

              <Typography.Text style={{ fontSize: 8 }}>
                {dayjs(item.createdAt).fromNow()}
              </Typography.Text>
            </StyledFlex>
          ),
          description: (
            <StyledFlex style={{ gap: 4 }}>
              <PaymentHistoryStatus status={item.status} size="small" />
              <PaymentHistoryState state={item.state} size="small" />
            </StyledFlex>
          ),
          avatar: (
            <AppAvatar.Group>
              <ProviderLogo provider={item?.provider} />
              <CustomerLogo customer={item?.customer} />
            </AppAvatar.Group>
          ),
        }))}
        extra={<MonthlyFilters value={[firstDay, lastDay]} />}
      />

      <PaymentHistoryDrawer
        open={openDrawer}
        toggle={toggle}
        handleSelect={handleSelect}
        selected={selected}
      />
    </>
  );
}
